import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { apiFetch } from '../utils/api';
import { Icons } from '../components/Icons';

function PayrollReports({ org }) {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportQuarter, setReportQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [remittancePeriod, setRemittancePeriod] = useState('monthly'); // 'monthly' | 'quarterly'
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [pd7aData, setPd7aData] = useState({
    grossPayroll: 0,
    numberOfEmployees: 0,
    totalCppRemittance: 0,
    employeeCPP: 0,
    employerCPP: 0,
    totalEiRemittance: 0,
    employeeEI: 0,
    employerEI: 0,
    totalIncomeTax: 0,
    totalRemittanceDue: 0,
    monthlyBreakdown: [],
    quarterlyBreakdown: [],
    monthlySubBreakdown: [],
    records: [],
    hasRecords: false,
    dueDate: '',
    periodLabel: '',
    isFullYear: false,
    periodType: 'monthly'
  });

  const triggerNotification = (message, type = 'success') => {
    const options = { position: "top-right", autoClose: 5000, hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true };
    switch (type) {
      case 'success': toast.success(message, options); break;
      case 'danger': toast.error(message, options); break;
      case 'warning': toast.warn(message, options); break;
      case 'info': toast.info(message, options); break;
      default: toast(message, options); break;
    }
  };

  const fetchData = useCallback(async () => {
    if (!org?.id) return;
    setLoading(true);
    try {
      const empData = await apiFetch(`/organizations/${org.id}/employees?year=${reportYear}`, org.tenantId);
      setEmployees(empData);

      const payData = await apiFetch(`/organizations/${org.id}/payroll-records?year=${reportYear}`, org.tenantId);
      setPayrollRecords(payData);
    } catch (error) {
      triggerNotification('Network error loading reporting data.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [org?.id, reportYear, org?.tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- PD7A Calculation Logic ---
  useEffect(() => {
    const calculatePD7A = () => {
      const recordsExist = payrollRecords && payrollRecords.length > 0;

      if (!recordsExist) {
        setPd7aData({
          hasRecords: false,
          monthlyBreakdown: [],
          quarterlyBreakdown: [],
          monthlySubBreakdown: [],
          records: [],
          totalRemittanceDue: 0,
          grossPayroll: 0,
          numberOfEmployees: 0,
          totalCppRemittance: 0,
          employeeCPP: 0,
          employerCPP: 0,
          totalEiRemittance: 0,
          employeeEI: 0,
          employerEI: 0,
          totalIncomeTax: 0,
          periodType: remittancePeriod
        });
        return;
      }

      if (remittancePeriod === 'quarterly') {
        const quarterInfo = [
          { q: 1, label: "Quarter 1 (Jan - Mar)", startM: 1, endM: 3, dueDate: `April 15, ${reportYear}` },
          { q: 2, label: "Quarter 2 (Apr - Jun)", startM: 4, endM: 6, dueDate: `July 15, ${reportYear}` },
          { q: 3, label: "Quarter 3 (Jul - Sep)", startM: 7, endM: 9, dueDate: `October 15, ${reportYear}` },
          { q: 4, label: "Quarter 4 (Oct - Dec)", startM: 10, endM: 12, dueDate: `January 15, ${reportYear + 1}` }
        ];

        if (reportQuarter === 0) {
          // Full Year Quarterly
          const quarterlyBreakdown = quarterInfo.map(({ q, label, startM, endM, dueDate }) => {
            const qRecords = payrollRecords.filter(r => {
              const payDate = new Date(r.pay_date);
              const m = payDate.getMonth() + 1;
              return r.status === 'Final' && payDate.getFullYear() === reportYear && m >= startM && m <= endM;
            });

            const empCount = new Set(qRecords.map(r => r.employee_id)).size;
            const grossPayroll = qRecords.reduce((sum, r) => sum + (r.gross_pay || 0), 0);
            const employeeCPP = qRecords.reduce((sum, r) => sum + (r.cpp_contribution || 0), 0);
            const employeeEI = qRecords.reduce((sum, r) => sum + (r.ei_premium || 0), 0);
            const incomeTax = qRecords.reduce((sum, r) => sum + (r.federal_tax_deduction || 0) + (r.provincial_tax_deduction || 0), 0);
            const employerCPP = employeeCPP;
            const employerEI = employeeEI * 1.4;
            const totalRemittance = employeeCPP + employerCPP + employeeEI + employerEI + incomeTax;

            return {
              quarter: q,
              quarterLabel: label,
              dueDate,
              employeeCount: empCount,
              grossPayroll,
              employeeCPP,
              employerCPP,
              employeeEI,
              employerEI,
              incomeTax,
              totalRemittance
            };
          });

          const totalDue = quarterlyBreakdown.reduce((sum, q) => sum + q.totalRemittance, 0);
          const hasAny = quarterlyBreakdown.some(q => q.grossPayroll > 0 || q.totalRemittance > 0);

          setPd7aData({
            hasRecords: hasAny,
            isFullYear: true,
            periodType: 'quarterly',
            quarterlyBreakdown,
            totalRemittanceDue: totalDue
          });
        } else {
          // Single Quarter
          const activeQ = quarterInfo.find(qi => qi.q === reportQuarter) || quarterInfo[0];
          const qRecords = payrollRecords.filter(r => {
            const payDate = new Date(r.pay_date);
            const m = payDate.getMonth() + 1;
            return r.status === 'Final' && payDate.getFullYear() === reportYear && m >= activeQ.startM && m <= activeQ.endM;
          });

          if (qRecords.length === 0) {
            setPd7aData({
              hasRecords: false,
              isFullYear: false,
              periodType: 'quarterly',
              periodLabel: activeQ.label,
              dueDate: activeQ.dueDate,
              totalRemittanceDue: 0
            });
            return;
          }

          const monthlySubBreakdown = [];
          for (let m = activeQ.startM; m <= activeQ.endM; m++) {
            const mRecs = qRecords.filter(r => (new Date(r.pay_date).getMonth() + 1) === m);
            const mEmpCount = new Set(mRecs.map(r => r.employee_id)).size;
            const mGross = mRecs.reduce((sum, r) => sum + (r.gross_pay || 0), 0);
            const mEmpCPP = mRecs.reduce((sum, r) => sum + (r.cpp_contribution || 0), 0);
            const mEmpEI = mRecs.reduce((sum, r) => sum + (r.ei_premium || 0), 0);
            const mTax = mRecs.reduce((sum, r) => sum + (r.federal_tax_deduction || 0) + (r.provincial_tax_deduction || 0), 0);
            const mEmprCPP = mEmpCPP;
            const mEmprEI = mEmpEI * 1.4;

            monthlySubBreakdown.push({
              month: m,
              monthName: new Date(reportYear, m - 1, 1).toLocaleString('default', { month: 'long' }),
              employeeCount: mEmpCount,
              grossPayroll: mGross,
              employeeCPP: mEmpCPP,
              employerCPP: mEmprCPP,
              employeeEI: mEmpEI,
              employerEI: mEmprEI,
              incomeTax: mTax,
              totalRemittance: mEmpCPP + mEmprCPP + mEmpEI + mEmprEI + mTax
            });
          }

          const distinctEmpIds = new Set(qRecords.map(r => r.employee_id));
          const grossPayroll = qRecords.reduce((sum, r) => sum + (r.gross_pay || 0), 0);
          const employeeCPP = qRecords.reduce((sum, r) => sum + (r.cpp_contribution || 0), 0);
          const employeeEI = qRecords.reduce((sum, r) => sum + (r.ei_premium || 0), 0);
          const incomeTax = qRecords.reduce((sum, r) => sum + (r.federal_tax_deduction || 0) + (r.provincial_tax_deduction || 0), 0);

          const employerCPP = employeeCPP;
          const employerEI = employeeEI * 1.4;
          const totalCppRemittance = employeeCPP + employerCPP;
          const totalEiRemittance = employeeEI + employerEI;
          const totalRemittanceDue = totalCppRemittance + totalEiRemittance + incomeTax;

          setPd7aData({
            hasRecords: true,
            isFullYear: false,
            periodType: 'quarterly',
            periodLabel: activeQ.label,
            dueDate: activeQ.dueDate,
            grossPayroll,
            numberOfEmployees: distinctEmpIds.size,
            employeeCPP,
            employerCPP,
            totalCppRemittance,
            employeeEI,
            employerEI,
            totalEiRemittance,
            totalIncomeTax: incomeTax,
            totalRemittanceDue,
            monthlySubBreakdown,
            records: qRecords
          });
        }
      } else {
        // Monthly
        const isFullYear = reportMonth === 0;
        if (isFullYear) {
          const monthlyBreakdown = [];
          for (let i = 1; i <= 12; i++) {
            const monthRecords = payrollRecords.filter(r => {
              const payDate = new Date(r.pay_date);
              return r.status === 'Final' && 
                     payDate.getFullYear() === reportYear && 
                     (payDate.getMonth() + 1) === i;
            });

            if (monthRecords.length > 0) {
              const employeeCPP = monthRecords.reduce((sum, r) => sum + (r.cpp_contribution || 0), 0);
              const employeeEI = monthRecords.reduce((sum, r) => sum + (r.ei_premium || 0), 0);
              const incomeTax = monthRecords.reduce((sum, r) => sum + (r.federal_tax_deduction || 0) + (r.provincial_tax_deduction || 0), 0);
              const employerCPP = employeeCPP;
              const employerEI = employeeEI * 1.4;
              
              const employeeCount = new Set(monthRecords.map(r => r.employee_id)).size;
              const grossPayroll = monthRecords.reduce((sum, r) => sum + (r.gross_pay || 0), 0);
              const dueDate = i === 12 ? `January 15, ${reportYear + 1}` : `${new Date(reportYear, i, 1).toLocaleString('default', { month: 'long' })} 15, ${reportYear}`;

              monthlyBreakdown.push({
                month: i,
                monthName: new Date(reportYear, i - 1, 1).toLocaleString('default', { month: 'long' }),
                dueDate,
                employeeCPP,
                employerCPP,
                employeeEI,
                employerEI,
                incomeTax,
                totalRemittance: employeeCPP + employerCPP + employeeEI + employerEI + incomeTax,
                employeeCount,
                grossPayroll,
              });
            }
          }
          setPd7aData({
            hasRecords: monthlyBreakdown.length > 0,
            isFullYear: true,
            periodType: 'monthly',
            monthlyBreakdown,
            totalRemittanceDue: monthlyBreakdown.reduce((sum, m) => sum + m.totalRemittance, 0)
          });
        } else {
          // Single Month
          const filteredRecords = payrollRecords.filter(r => {
            const payDate = new Date(r.pay_date);
            return r.status === 'Final' && 
                   payDate.getFullYear() === reportYear && (payDate.getMonth() + 1) === reportMonth;
          });

          const monthName = new Date(reportYear, reportMonth - 1, 1).toLocaleString('default', { month: 'long' });
          const dueDate = reportMonth === 12 ? `January 15, ${reportYear + 1}` : `${new Date(reportYear, reportMonth, 1).toLocaleString('default', { month: 'long' })} 15, ${reportYear}`;

          if (filteredRecords.length === 0) {
            setPd7aData({
              hasRecords: false,
              isFullYear: false,
              periodType: 'monthly',
              periodLabel: `${monthName} ${reportYear}`,
              dueDate,
              totalRemittanceDue: 0
            });
            return;
          }

          const employeeIds = new Set(filteredRecords.map(r => r.employee_id));
          const grossPayroll = filteredRecords.reduce((sum, r) => sum + (r.gross_pay || 0), 0);
          const employeeCPP = filteredRecords.reduce((sum, r) => sum + (r.cpp_contribution || 0), 0);
          const employeeEI = filteredRecords.reduce((sum, r) => sum + (r.ei_premium || 0), 0);
          const incomeTax = filteredRecords.reduce((sum, r) => sum + (r.federal_tax_deduction || 0) + (r.provincial_tax_deduction || 0), 0);

          const employerCPP = employeeCPP;
          const employerEI = employeeEI * 1.4;
          const totalCppRemittance = employeeCPP + employerCPP;
          const totalEiRemittance = employeeEI + employerEI;
          const totalRemittanceDue = totalCppRemittance + totalEiRemittance + incomeTax;

          setPd7aData({
            hasRecords: true,
            isFullYear: false,
            periodType: 'monthly',
            periodLabel: `${monthName} ${reportYear}`,
            dueDate,
            grossPayroll,
            numberOfEmployees: employeeIds.size,
            employeeCPP,
            employerCPP,
            totalCppRemittance,
            employeeEI,
            employerEI,
            totalEiRemittance,
            totalIncomeTax: incomeTax,
            totalRemittanceDue,
            records: filteredRecords
          });
        }
      }
    };
    calculatePD7A();
  }, [payrollRecords, reportYear, reportMonth, remittancePeriod, reportQuarter]);

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown Employee';
  };

  // --- CSV GENERATOR UTILITY ---
  const downloadFile = (csvContent, fileName) => {
    try {
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // triggerNotification(`${fileName} exported successfully.`);
    } catch (err) {
      triggerNotification('Failed to generate local file export.', 'danger');
    }
  };

  // --- REPORT 1: COMPREHENSIVE DEDUCTIONS EXPORT ---
  const generateComprehensiveReport = (currentOrg) => {
    if (payrollRecords.length === 0) {
      triggerNotification(`No processed records found for ${reportYear}.`, 'warning');
      return;
    }

    // Define CSV Headers
    let csv = "Employee Name,Pay Date,Period Start,Period End,Gross Pay,Federal Tax,Provincial Tax,CPP,EI,Total Deductions,Net Pay,Status\n";

    // Sort records chronologically
    const sortedRecords = [...payrollRecords].sort((a, b) => new Date(a.pay_date) - new Date(b.pay_date));

    sortedRecords.forEach(record => {
      const empName = getEmployeeName(record.employee_id);
      const gross = record.gross_pay || 0;
      const fed = record.federal_tax_deduction || 0;
      const prov = record.provincial_tax_deduction || 0;
      const cpp = record.cpp_contribution || 0;
      const ei = record.ei_premium || 0;
      const totalDed = record.total_deductions || 0;
      const net = record.net_pay || 0;

      // Wrap string fields in quotes to prevent CSV breaking on commas
      csv += `"${empName}",${record.pay_date || ''},${record.period_start_date || ''},${record.period_end_date || ''},${gross.toFixed(2)},${fed.toFixed(2)},${prov.toFixed(2)},${cpp.toFixed(2)},${ei.toFixed(2)},${totalDed.toFixed(2)},${net.toFixed(2)},${record.status}\n`;
    });

    downloadFile(csv, `${currentOrg.name}_Comprehensive_Payroll_Report_${reportYear}.csv`);
  };

  const handleApiDownload = async (url, defaultFileName, currentOrg) => {
    try {
      const response = await apiFetch(url, org.tenantId, { parseResponse: false });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      const disposition = response.headers.get('content-disposition');
      let fileName = defaultFileName;
      if (disposition && disposition.indexOf('attachment') !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
          fileName = matches[1].replace(/['"]/g, '');
        }
      }

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      triggerNotification(error.message, 'danger');
    }
  };

  // --- Official CRA Backend Exports ---
  const handleExportCRAT4XML = (currentOrg) => handleApiDownload(`/organizations/${currentOrg.id}/t4/xml?year=${reportYear}`, `${currentOrg.name}_T4_Export_${reportYear}.xml`, currentOrg);
  const handleExportCRAT4PDF = (currentOrg) => handleApiDownload(`/organizations/${currentOrg.id}/t4/pdf?year=${reportYear}&employee_id=${selectedEmployee}`, `${currentOrg.name}_T4_PDF_Export_${reportYear}.zip`, currentOrg);
  const handleExportCRAT4AXML = (currentOrg) => handleApiDownload(`/organizations/${currentOrg.id}/t4a/xml?year=${reportYear}`, `${currentOrg.name}_T4A_Export_${reportYear}.xml`, currentOrg);
  const handleExportCRAT4APDF = (currentOrg) => handleApiDownload(`/organizations/${currentOrg.id}/t4a/pdf?year=${reportYear}&employee_id=${selectedEmployee}`, `${currentOrg.name}_T4A_PDF_Export_${reportYear}.zip`, currentOrg);
  const handleExportPD7APDF = (currentOrg) => {
    if (remittancePeriod === 'quarterly') {
      const qSuffix = reportQuarter === 0 ? 'All' : reportQuarter;
      return handleApiDownload(
        `/organizations/${currentOrg.id}/pd7a/pdf?year=${reportYear}&period_type=quarterly&quarter=${reportQuarter}`,
        `${currentOrg.name}_PD7A_Report_${reportYear}_Q${qSuffix}.pdf`,
        currentOrg
      );
    }
    const mSuffix = reportMonth === 0 ? 'FullYear' : reportMonth;
    return handleApiDownload(
      `/organizations/${currentOrg.id}/pd7a/pdf?year=${reportYear}&period_type=monthly&month=${reportMonth}`,
      `${currentOrg.name}_PD7A_Report_${reportYear}-${mSuffix}.pdf`,
      currentOrg
    );
  };

  const handleExportPD7AExcel = (currentOrg) => {
    if (remittancePeriod === 'quarterly') {
      const qSuffix = reportQuarter === 0 ? 'All' : reportQuarter;
      return handleApiDownload(
        `/organizations/${currentOrg.id}/pd7a/excel?year=${reportYear}&period_type=quarterly&quarter=${reportQuarter}`,
        `${currentOrg.name}_PD7A_Report_${reportYear}_Q${qSuffix}.xlsx`,
        currentOrg
      );
    }
    const mSuffix = reportMonth === 0 ? 'FullYear' : reportMonth;
    return handleApiDownload(
      `/organizations/${currentOrg.id}/pd7a/excel?year=${reportYear}&period_type=monthly&month=${reportMonth}`,
      `${currentOrg.name}_PD7A_Report_${reportYear}-${mSuffix}.xlsx`,
      currentOrg
    );
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
  
  // const finalRecordsCount = payrollRecords.filter(r => r.status === 'Final').length;
  // const activeT4Employees = new Set(payrollRecords.filter(r => r.status === 'Final').map(r => r.employee_id)).size;

  if (!org) return null;

  return (
    <div className="container-fluid py-4 px-md-4" style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      {/* Add ToastContainer here. Ideally, this should be in your root App.js */}
      <ToastContainer />

      {/* Page Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div>
          <div className="d-flex align-items-center mb-1">
            <Icons.FileCheck />
            <h1 className="h4 fw-bold text-dark mb-0">Reporting & Compliance</h1>
          </div>
          <p className="text-muted small mb-0 mt-1">Export structured payroll ledgers and generate annual compliance summaries.</p>
        </div>
        
        {/* Global Year Selector */}
        <div className="d-flex align-items-center bg-white border border-light rounded-3 px-3 py-2 shadow-sm">
          <label className="fw-semibold me-3 text-muted small text-uppercase tracking-wider mb-0">Fiscal Year</label>
          <select
            className="form-select form-select-sm border-0 fw-bold bg-transparent shadow-none p-0 pe-4"
            value={reportYear}
            onChange={(e) => setReportYear(parseInt(e.target.value, 10))}
            style={{ minWidth: '80px', cursor: 'pointer', fontSize: '1rem' }}
          >
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
         <div className="card border-0 shadow-sm rounded-3 bg-white p-5 text-center text-muted d-flex flex-column align-items-center">
           <div className="spinner-border text-primary mb-3" role="status"></div>
           <span>Compiling reporting data for {reportYear}...</span>
         </div>
      ) : (
        <div className="row g-4 mb-5">
          
          {/* Report Card 1: Comprehensive Export */}
          <div className="col-lg-6 col-xl-4">
            <div className="card border-0 shadow-sm rounded-4 h-100 bg-white" style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
              <div className="card-body p-4 p-xl-5 d-flex flex-column">
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '56px', height: '56px' }}>
                    <Icons.Document />
                  </div>
                  <h2 className="h5 fw-bold text-dark mb-0">Payroll Ledger Export</h2>
                </div>
                
                <p className="text-muted small flex-grow-1" style={{ lineHeight: '1.6' }}>
                  A comprehensive Excel-ready CSV dump of all employee payroll records for {reportYear}. Includes detailed breakdowns of gross pay, federal/provincial taxes, CPP, EI, and net pay mapped per pay period.
                </p>
                
                <div className="mt-4 pt-4 border-top d-flex justify-content-between align-items-center">
                  <div className="d-flex flex-column">
                    <span className="text-dark fw-bold">{payrollRecords.length}</span>
                    <span className="text-muted text-uppercase tracking-wider" style={{ fontSize: '0.65rem' }}>Total Records</span>
                  </div>
                  <button 
                    onClick={() => generateComprehensiveReport(org)} 
                    className="btn btn-outline-primary fw-semibold px-4 d-flex align-items-center"
                    disabled={payrollRecords.length === 0}
                  >
                    <Icons.Download /> Download CSV
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Report Card 2: XML E-Filing */}
          <div className="col-lg-6 col-xl-4">
            <div className="card border-0 shadow-sm rounded-4 h-100 bg-white" style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
              <div className="card-body p-4 p-xl-5 d-flex flex-column">
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '56px', height: '56px' }}>
                    <Icons.Code />
                  </div>
                  <h2 className="h5 fw-bold text-dark mb-0">CRA E-Filing (XML)</h2>
                </div>
                
                <p className="text-muted small flex-grow-1" style={{ lineHeight: '1.6' }}>
                  Generate the official XML payloads required for bulk upload to the CRA online portal.
                </p>
                
                <div className="mt-auto pt-4 border-top d-flex flex-column gap-2">
                  <button className="btn btn-outline-primary fw-semibold w-100" onClick={() => handleExportCRAT4XML(org)}>
                    <Icons.Download /> Download T4 XML
                  </button>
                  <button className="btn btn-outline-info text-dark border-info fw-semibold w-100" onClick={() => handleExportCRAT4AXML(org)}>
                    <Icons.Download /> Download T4A XML
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Report Card 3: PDF Slips */}
          <div className="col-lg-6 col-xl-4">
            <div className="card border-0 shadow-sm rounded-4 h-100 bg-white" style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
              <div className="card-body p-4 p-xl-5 d-flex flex-column">
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '56px', height: '56px' }}>
                    <Icons.UserDocument />
                  </div>
                  <h2 className="h5 fw-bold text-dark mb-0">Employee & Summary Slips (PDF)</h2>
                </div>
                
                <p className="text-muted small flex-grow-1" style={{ lineHeight: '1.6' }}>
                  Generate printable, formatted PDF slips for individual employees or download the official CRA summary reports.
                </p>
                
                <select className="form-select bg-light border-0 shadow-sm mb-3" value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)}>
                  <option value="">Select an employee for individual slip...</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name} {emp.is_t4a === 1 ? '(T4A)' : '(T4)'}</option>
                  ))}
                </select>

                <div className="mt-auto pt-4 border-top d-flex flex-column gap-2">
                  <button className="btn btn-primary fw-semibold w-100" onClick={() => handleExportCRAT4PDF(org)}>
                    <Icons.Download /> {selectedEmployee ? 'Download T4 PDF' : 'Download T4 Package (ZIP)'}
                  </button>
                  <button className="btn btn-info text-dark fw-semibold w-100" onClick={() => handleExportCRAT4APDF(org)}>
                    <Icons.Download /> {selectedEmployee ? 'Download T4A PDF' : 'Download T4A Package (ZIP)'}
                  </button>
                  <button className="btn btn-danger fw-semibold w-100" onClick={() => handleApiDownload(`/organizations/${org.id}/t4/summary/pdf?year=${reportYear}`, `${org.name}_T4_Summary_${reportYear}.pdf`, org)}>
                    <Icons.Download /> T4 Summary PDF
                  </button>
                  <button className="btn btn-warning text-dark border-warning fw-semibold w-100" onClick={() => handleApiDownload(`/organizations/${org.id}/t4a/summary/pdf?year=${reportYear}`, `${org.name}_T4A_Summary_${reportYear}.pdf`, org)}>
                    <Icons.Download /> T4A Summary PDF
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Report Card 4: PD7A Remittance Summary */}
          <div className="col-lg-6 col-xl-12">
            <div className="card border-0 shadow-sm rounded-4 h-100 bg-white">
              <div className="card-body p-4 p-xl-5 d-flex flex-column">
                {/* Header */}
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-4 gap-2">
                  <div className="d-flex align-items-center">
                    <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '56px', height: '56px' }}>
                      <Icons.FileCheck />
                    </div>
                    <div>
                      <h2 className="h5 fw-bold text-dark mb-0">PD7A Remittance Summary</h2>
                      <p className="text-muted small mb-0">CRA source deduction remittance for Monthly and Quarterly customers.</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-light text-muted border px-2 py-2 fw-normal small d-none d-sm-inline-block">
                      Cheque Date Basis (CRA)
                    </span>
                    <span className={`badge px-3 py-2 rounded-pill fw-semibold ${remittancePeriod === 'quarterly' ? 'bg-primary text-white' : 'bg-success text-white'}`}>
                      {remittancePeriod === 'quarterly' ? 'Quarterly Remitter' : 'Monthly Remitter'}
                    </span>
                  </div>
                </div>

                {/* Remittance Period Configuration */}
                <div className="p-3 mb-4 rounded-3 border bg-light bg-opacity-50">
                  <label className="form-label fw-bold text-dark small text-uppercase mb-3 d-flex align-items-center">
                    <span className="me-2 text-primary">●</span> Select Remittance Period
                  </label>

                  {/* Frequency Selector */}
                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <div
                        className={`p-2 border rounded-3 text-center ${remittancePeriod === 'monthly' ? 'border-success bg-success bg-opacity-10 shadow-sm' : 'bg-white'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setRemittancePeriod('monthly')}
                      >
                        <div className="form-check d-inline-block mb-0">
                          <input
                            className="form-check-input me-2"
                            type="radio"
                            name="remitFreq"
                            id="freqMonthly"
                            checked={remittancePeriod === 'monthly'}
                            onChange={() => setRemittancePeriod('monthly')}
                          />
                          <label className="form-check-label fw-bold text-dark small cursor-pointer" htmlFor="freqMonthly">
                            Monthly
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="col-6">
                      <div
                        className={`p-2 border rounded-3 text-center ${remittancePeriod === 'quarterly' ? 'border-primary bg-primary bg-opacity-10 shadow-sm' : 'bg-white'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setRemittancePeriod('quarterly')}
                      >
                        <div className="form-check d-inline-block mb-0">
                          <input
                            className="form-check-input me-2"
                            type="radio"
                            name="remitFreq"
                            id="freqQuarterly"
                            checked={remittancePeriod === 'quarterly'}
                            onChange={() => setRemittancePeriod('quarterly')}
                          />
                          <label className="form-check-label fw-bold text-dark small cursor-pointer" htmlFor="freqQuarterly">
                            Quarterly
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Monthly Specific Selection */}
                  {remittancePeriod === 'monthly' && (
                    <div className="row g-2 align-items-center">
                      <div className="col-md-7">
                        <label className="form-label text-muted small mb-1 fw-semibold">Remittance Month</label>
                        <select
                          className="form-select form-select-sm"
                          value={reportMonth}
                          onChange={(e) => setReportMonth(parseInt(e.target.value, 10))}
                        >
                          <option value="0">Full Year (12 Months Breakdown)</option>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                            <option key={m} value={m}>
                              {String(m).padStart(2, '0')} - {new Date(0, m - 1).toLocaleString('default', { month: 'long' })}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-5 pt-md-4">
                        <button
                          type="button"
                          className={`btn btn-sm w-100 fw-semibold ${reportMonth === 0 ? 'btn-success text-white' : 'btn-outline-success'}`}
                          onClick={() => setReportMonth(reportMonth === 0 ? (new Date().getMonth() + 1) : 0)}
                        >
                          {reportMonth === 0 ? '✓ Whole Year Selected' : 'Run for whole year (Monthly)'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Quarterly Specific Selection */}
                  {remittancePeriod === 'quarterly' && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label text-muted small mb-0 fw-semibold">Remittance Quarter</label>
                        <span className="badge bg-info bg-opacity-10 text-primary small">4 Calendar Quarters</span>
                      </div>
                      <div className="row g-2 mb-3">
                        {[
                          { q: 1, name: 'Quarter 1', range: 'Jan-Mar', due: 'April 15' },
                          { q: 2, name: 'Quarter 2', range: 'Apr-Jun', due: 'July 15' },
                          { q: 3, name: 'Quarter 3', range: 'Jul-Sep', due: 'October 15' },
                          { q: 4, name: 'Quarter 4', range: 'Oct-Dec', due: 'January 15' },
                        ].map(({ q, name, range, due }) => (
                          <div key={q} className="col-6 col-lg-3">
                            <div
                              className={`p-2 border rounded-3 h-100 ${reportQuarter === q ? 'border-primary bg-primary bg-opacity-10 shadow-sm' : 'bg-white'}`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setReportQuarter(q)}
                            >
                              <div className="form-check mb-1">
                                <input
                                  className="form-check-input me-2"
                                  type="radio"
                                  name="quarterSelector"
                                  id={`quarter_${q}`}
                                  checked={reportQuarter === q}
                                  onChange={() => setReportQuarter(q)}
                                />
                                <label className="form-check-label fw-bold text-dark small cursor-pointer" htmlFor={`quarter_${q}`}>
                                  {name}
                                </label>
                              </div>
                              <div className="text-muted small ps-4 mb-1" style={{ fontSize: '0.78rem' }}>
                                ({range})
                              </div>
                              <div className="small ps-4" style={{ fontSize: '0.72rem' }}>
                                Due: <span className="fw-semibold text-primary">{due}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        className={`btn btn-sm w-100 fw-semibold ${reportQuarter === 0 ? 'btn-primary text-white' : 'btn-outline-primary'}`}
                        onClick={() => setReportQuarter(reportQuarter === 0 ? Math.ceil((new Date().getMonth() + 1) / 3) : 0)}
                      >
                        {reportQuarter === 0 ? '✓ Whole Year Selected (Quarterly)' : 'Run for whole year (Quarterly)'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Section 3: Remittance Data Results */}
                {pd7aData.hasRecords ? (
                  !pd7aData.isFullYear ? (
                    /* Single Period (Month or Quarter) View */
                    <div className="flex-grow-1">
                      {/* Period & Due Date Pill */}
                      <div className="d-flex flex-wrap justify-content-between align-items-center p-3 mb-3 bg-light rounded-3 border">
                        <div>
                          <span className="text-muted small text-uppercase fw-semibold d-block">Reporting Period</span>
                          <strong className="text-dark h6 mb-0">{pd7aData.periodLabel}</strong>
                        </div>
                        {pd7aData.dueDate && (
                          <div className="text-end">
                            <span className="badge bg-warning text-dark px-3 py-2 fw-semibold">
                              CRA Remittance Due: {pd7aData.dueDate}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Summary Metrics */}
                      <ul className="list-group list-group-flush mb-4">
                        <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                          <span className="text-muted">Gross Payroll for {pd7aData.numberOfEmployees} Employee(s)</span>
                          <span className="fw-bold text-dark">{(pd7aData.grossPayroll || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                          <span className="text-muted">Income Tax Withheld</span>
                          <span className="fw-bold text-dark">{(pd7aData.totalIncomeTax || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                          <span className="text-muted">Total CPP Remittance (Employee + Employer)</span>
                          <span className="fw-bold text-dark">{(pd7aData.totalCppRemittance || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                          <span className="text-muted">Total EI Remittance (Employee + Employer at 1.4x)</span>
                          <span className="fw-bold text-dark">{(pd7aData.totalEiRemittance || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center px-3 py-3 bg-primary bg-opacity-10 mt-2 rounded-3 border border-primary border-opacity-25">
                          <div>
                            <strong className="text-primary d-block">Total Remittance Due</strong>
                            <small className="text-muted">Payable to Receiver General for Canada</small>
                          </div>
                          <strong className="text-primary h4 mb-0">{(pd7aData.totalRemittanceDue || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</strong>
                        </li>
                      </ul>

                      {/* If Quarterly: Monthly Sub-Breakdown for the 3 months in this Quarter */}
                      {remittancePeriod === 'quarterly' && pd7aData.monthlySubBreakdown && pd7aData.monthlySubBreakdown.length > 0 && (
                        <div className="mb-4">
                          <h6 className="fw-bold text-dark mb-2">Monthly Sub-Breakdown for {pd7aData.periodLabel}</h6>
                          <div className="table-responsive">
                            <table className="table table-sm table-bordered align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th className="small">Month</th>
                                  <th className="small text-end">Employees</th>
                                  <th className="small text-end">Gross Pay</th>
                                  <th className="small text-end">Emp CPP</th>
                                  <th className="small text-end">Empr CPP</th>
                                  <th className="small text-end">Emp EI</th>
                                  <th className="small text-end">Empr EI</th>
                                  <th className="small text-end">Income Tax</th>
                                  <th className="small text-end fw-bold text-primary">Remittance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pd7aData.monthlySubBreakdown.map(m => (
                                  <tr key={m.month}>
                                    <td className="fw-semibold">{m.monthName}</td>
                                    <td className="text-end">{m.employeeCount}</td>
                                    <td className="text-end">{(m.grossPayroll || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                    <td className="text-end">{(m.employeeCPP || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                    <td className="text-end">{(m.employerCPP || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                    <td className="text-end">{(m.employeeEI || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                    <td className="text-end">{(m.employerEI || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                    <td className="text-end">{(m.incomeTax || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                    <td className="text-end fw-bold text-primary">{(m.totalRemittance || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Full Year Breakdown Table (Monthly or Quarterly) */
                    <div className="flex-grow-1 table-responsive mb-3">
                      {remittancePeriod === 'quarterly' && pd7aData.quarterlyBreakdown ? (
                        /* 4 Quarters Table */
                        <table className="table table-sm table-hover align-middle">
                          <thead className="table-light">
                            <tr>
                              <th className="text-muted small">Quarter</th>
                              <th className="text-muted small">CRA Due Date</th>
                              <th className="text-muted small text-end">Employees</th>
                              <th className="text-muted small text-end">Gross Payroll</th>
                              <th className="text-muted small text-end">Emp CPP</th>
                              <th className="text-muted small text-end">Empr CPP</th>
                              <th className="text-muted small text-end">Emp EI</th>
                              <th className="text-muted small text-end">Empr EI</th>
                              <th className="text-muted small text-end">Income Tax</th>
                              <th className="text-primary text-end">Total Remittance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pd7aData.quarterlyBreakdown.map(q => (
                              <tr key={q.quarter}>
                                <td className="fw-bold">{q.quarterLabel}</td>
                                <td><span className="badge bg-secondary bg-opacity-10 text-dark">{q.dueDate}</span></td>
                                <td className="text-end">{q.employeeCount}</td>
                                <td className="text-end">{q.grossPayroll.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                <td className="text-end">{q.employeeCPP.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                <td className="text-end">{q.employerCPP.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                <td className="text-end">{q.employeeEI.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                <td className="text-end">{q.employerEI.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                <td className="text-end">{q.incomeTax.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                <td className="fw-bold text-end text-primary">{q.totalRemittance.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        /* 12 Months Table */
                        <table className="table table-sm table-hover align-middle">
                          <thead className="table-light">
                            <tr>
                              <th className="text-muted small">Month</th>
                              <th className="text-muted small">CRA Due Date</th>
                              <th className="text-muted small text-end">Employees</th>
                              <th className="text-muted small text-end">Gross Payroll</th>
                              <th className="text-muted small text-end">Emp CPP</th>
                              <th className="text-muted small text-end">Empr CPP</th>
                              <th className="text-muted small text-end">Emp EI</th>
                              <th className="text-muted small text-end">Empr EI</th>
                              <th className="text-muted small text-end">Income Tax</th>
                              <th className="text-primary text-end">Total Remittance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pd7aData.monthlyBreakdown.map(m => (
                              <tr key={m.month}>
                                <td className="fw-bold">{m.monthName}</td>
                                <td><span className="badge bg-secondary bg-opacity-10 text-dark">{m.dueDate}</span></td>
                                <td className="text-end">{m.employeeCount}</td>
                                <td className="text-end">{m.grossPayroll.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                <td className="text-end">{m.employeeCPP.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                <td className="text-end">{m.employerCPP.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                <td className="text-end">{m.employeeEI.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                <td className="text-end">{m.employerEI.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                <td className="text-end">{m.incomeTax.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                                <td className="fw-bold text-end text-primary">{m.totalRemittance.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}

                      <div className="d-flex justify-content-between align-items-center px-3 py-3 bg-light mt-2 rounded-3 border">
                        <div>
                          <strong className="text-dark">Annual Remittance Due for {reportYear}</strong>
                          <div className="text-muted small">
                            {remittancePeriod === 'quarterly' ? 'Aggregated across 4 calendar quarters' : 'Aggregated across 12 calendar months'}
                          </div>
                        </div>
                        <strong className="text-primary h5 mb-0">{(pd7aData.totalRemittanceDue || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</strong>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="text-center text-muted py-5 flex-grow-1 d-flex flex-column align-items-center justify-content-center">
                    <div className="bg-light rounded-circle p-3 mb-2">
                      <Icons.FileCheck />
                    </div>
                    <span className="fw-semibold">No finalized payroll records found for the selected {remittancePeriod === 'quarterly' ? 'quarter' : 'month'}.</span>
                    <span className="small text-muted mt-1">Make sure payroll records for year {reportYear} are finalized.</span>
                  </div>
                )}

                {/* Section 4: Actions */}
                <div className="mt-4 pt-3 border-top d-flex flex-column flex-sm-row gap-3">
                  <button className="btn btn-success fw-semibold w-100" onClick={() => handleExportPD7APDF(org)} disabled={!pd7aData.hasRecords}>
                    <Icons.Download /> Download PDF
                  </button>
                  <button
                    className="btn btn-outline-success fw-semibold w-100"
                    onClick={() => handleExportPD7AExcel(org)}
                    disabled={!pd7aData.hasRecords}>
                    <Icons.Download /> Download Excel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PayrollReports;